import Vue from 'vue'
import VueRouter from 'vue-router'
import msDataProvider from "../services/msDataProvider"
import loginService from "../services/loginService"
import i18n from '@/lang/index'
import { StoreServiceInstance } from "@/services/storeService";

Vue.use(VueRouter)

async function nextIfNeed(toUrl, originalTo, next) {
    if (!toUrl || originalTo.path === toUrl) {
        next()
    } else {
        next(toUrl)
    }
}

function beforeEnterFactory(opts: any = {}) {
    return async (to, from, next) => {
        for (let i = 0; i < 1000; i++) {
            try {
                const setting = await msDataProvider.getData("setting")
                if (!setting.language) {
                    await StoreServiceInstance.setData('language', 'vi');
                }
                const backendServerName = await msDataProvider.executeCommand("backendServerNameGetCurrentValue")
                if (!backendServerName) {
                    return nextIfNeed("/login/first_time_login", to, next)
                }
                const authenInfo = await msDataProvider.getData("authenAuthenInfo")
                if (authenInfo.access_token_verifying) {
                    return nextIfNeed("/login/verify_current_session", to, next)
                }
                if (!authenInfo.user_info || !authenInfo.token_info.authentication_done) {
                    return nextIfNeed("/login/prepare", to, next)
                }
                const enrollmentEnrollmentStatus = await msDataProvider.getData("enrollmentEnrollmentStatus")
                if (!enrollmentEnrollmentStatus.sent) {
                    if (!enrollmentEnrollmentStatus.enrollment_request || !enrollmentEnrollmentStatus.enrollment_request.device_id) {
                        await new Promise(f => setTimeout(f, 1000))
                        continue
                    }
                    return nextIfNeed("/enrollment/prepare", to, next)
                } else if (!enrollmentEnrollmentStatus.fetched) {
                    if (enrollmentEnrollmentStatus.required_extra_action === "SmsOtp") {
                        return nextIfNeed("/enrollment/wait_sms_otp", to, next)
                    } else {
                        return nextIfNeed("/enrollment/wait", to, next)
                    }
                } else if (!enrollmentEnrollmentStatus.approved) {
                    return nextIfNeed("/enrollment/result", to, next)
                }
                if (opts.custom) {
                    return opts.custom(to, from, next)
                }
                return nextIfNeed(null, to, next)
            } catch(e) {
                console.error("error router:", e);
                return nextIfNeed(null, to, next)
            }
        }
    }
}

function loginBeforeEnter(name: string = "") {
    return async (to, from, next) => {
        if (name === "username_password") {
            return next()
        }
        if (!loginService.getPrepareLoginResponse()) {
            return next("/login/prepare")
        }
        return next()
    }
}

const router = new VueRouter({
    base: "/ui/",
    mode: "history",
    routes: [
        {
            path: "/",
            redirect: "/main",
        },
        {
            path: "/prepare",
            beforeEnter: beforeEnterFactory({
                custom(to, from, next) {
                    next("/main/connection")
                }
            }),
        },
        {
            path: "/common",
            component: () => import("../views/common/Layout.vue"),
            children: [
                {
                    path: "local_app_connect",
                    component: () => import("../views/common/LocalAppConnect.vue")
                },
                {
                    path: "local_service_connect",
                    component: () => import("../views/common/LocalServiceConnect.vue")
                },
                {
                    path: "sdp_disconnect",
                    component: () => import("../views/common/SDPDisconnect.vue")
                },
                {
                    path: "select-language",
                    component: () => import("../views/common/SelectLanguage.vue")
                },
            ]
        },
        {
            path: "/login",
            component: () => import("../views/login/Layout.vue"),
            children: [
                {
                    path: "verify_current_session",
                    component: () => import("../views/login/VerifyCurrentSession.vue"),
                    meta: {
                        hasBack: 'logout',
                        title: 'LOGIN.VERIFY_CURRENT_SESSION'
                    }
                },
                {
                    path: "prepare",
                    component: () => import("../views/login/Prepare.vue"),
                    meta: {
                        hasBack: 'logout',
                        title: 'LOGIN.TWO_FACTOR_AUTHENTICATION'
                    }
                },
                {
                    path: "first_time_login",
                    component: () => import("../views/login/FirstTimeLogin.vue"),
                },
                {
                    path: "forgot_password",
                    component: () => import("../views/login/ForgotPassword.vue"),
                    meta: {
                        hasBack: (router, obj) => {router.push(obj)},
                        title: 'LOGIN.FORGOT_PASSWORD_TITLE'
                    }
                },
                {
                    path: "select_server_name",
                    component: () => import("../views/login/SelectServerName.vue"),
                    meta: {
                        hasBack: true,
                        title: 'LOGIN.LOGIN_OPTIONS'
                    }
                },
                {
                    path: "username_password",
                    component: () => import("../views/login/UsernamePassword.vue"),
                    beforeEnter: loginBeforeEnter("username_password"),
                },
                {
                    path: "open_id_connect",
                    component: () => import("../views/login/OpenIdConnect.vue"),
                    beforeEnter: loginBeforeEnter(),
                },
                {
                    path: "totp",
                    component: () => import("../views/login/Totp.vue"),
                    beforeEnter: loginBeforeEnter(),
                    meta: {
                        hasBack: (router, obj) => {router.push(obj)},
                        title: 'LOGIN.TWO_FACTOR_AUTHENTICATION'
                    },
                },
                {
                    path: "sms_otp",
                    name: "sms_otp",
                    component: () => import("../views/login/SmsOtp.vue"),
                    beforeEnter: loginBeforeEnter(),
                    meta: {
                        hasBack: 'logout',
                        title: 'LOGIN.TWO_FACTOR_AUTHENTICATION'
                    }
                },
                {
                    path: "radius_otp",
                    name: "radius_otp",
                    component: () => import("../views/login/RadiusOtp.vue"),
                    beforeEnter: loginBeforeEnter(),
                    meta: {
                        hasBack: 'logout',
                        title: 'LOGIN.TWO_FACTOR_AUTHENTICATION'
                    }
                },
                {
                    path: "email_otp",
                    name: "email_otp",
                    component: () => import("../views/login/EmailOtp.vue"),
                    beforeEnter: loginBeforeEnter(),
                    meta: {
                        hasBack: 'logout',
                        title: 'LOGIN.TWO_FACTOR_AUTHENTICATION'
                    }
                },
                {
                    path: "security_key",
                    component: () => import("../views/login/SecurityKey.vue"),
                    beforeEnter: loginBeforeEnter(),
                    meta: {
                        hasBack: 'logout',
                        title: 'LOGIN.TWO_FACTOR_AUTHENTICATION'
                    }
                },
            ]
        },
        {
            path: "/enrollment",
            component: () => import("../views/enrollment/Layout.vue"),
            children: [
                {
                    path: "prepare",
                    component: () => import("../views/enrollment/Prepare.vue"),
                    meta: {
                        title: 'ENROLLMENT_LAYOUT.REGISTER_CONNECT'
                    },
                    beforeEnter: beforeEnterFactory({
                        async custom(to, from, next) {
                            const enrollmentEnrollmentStatus = await msDataProvider.getData("enrollmentEnrollmentStatus")
                            if (enrollmentEnrollmentStatus.fetched) {
                                next("/enrollment/result")
                            } else if (enrollmentEnrollmentStatus.sent) {
                                if (enrollmentEnrollmentStatus.required_extra_action === "SmsOtp") {
                                    next("/enrollment/wait_sms_otp")
                                } else {
                                    next("/enrollment/wait")
                                }
                            } else {
                                next()
                            }
                        },
                    })
                },
                {
                    path: "wait_sms_otp",
                    component: () => import("../views/enrollment/WaitSmsOtp.vue"),
                    beforeEnter: beforeEnterFactory({
                        async custom(to, from, next) {
                            const enrollmentEnrollmentStatus = await msDataProvider.getData("enrollmentEnrollmentStatus")
                            if (enrollmentEnrollmentStatus.fetched) {
                                next({name: "enrollment-result"})
                            } else {
                                next()
                            }
                        },
                    }),
                    meta: {
                        title: 'ENROLLMENT_LAYOUT.REGISTER_CONNECT'
                    }
                },
                {
                    path: "wait",
                    component: () => import("../views/enrollment/Wait.vue"),
                    beforeEnter: beforeEnterFactory({
                        async custom(to, from, next) {
                            const enrollmentEnrollmentStatus = await msDataProvider.getData("enrollmentEnrollmentStatus")
                            if (enrollmentEnrollmentStatus.fetched) {
                                next({name: "enrollment-result"})
                            } else {
                                next()
                            }
                        },
                    }),
                    meta: {
                        title: 'ENROLLMENT_LAYOUT.REGISTER_CONNECT'
                    }
                },
                {
                    path: "result",
                    name: "enrollment-result",
                    component: () => import("../views/enrollment/Result.vue"),
                    meta: {
                        title: 'ENROLLMENT_LAYOUT.REGISTER_CONNECT'
                    },
                    beforeEnter: beforeEnterFactory(),
                },
            ]
        },
        {
            path: "/main",
            component: () => import("../views/main/Layout.vue"),
            beforeEnter: beforeEnterFactory(),
            children: [
                {
                    path: "",
                    redirect: "connection",
                },
                {
                    path: "connection",
                    name: "connection",
                    component: () => import("../views/main/Connection.vue")
                },
                {
                    path: "notification",
                    name: "notification",
                    component: () => import("../views/main/Notification.vue")
                },
                {
                    path: "options",
                    name: "options",
                    component: () => import("../views/main/Options.vue")
                },
                {
                    path: "account",
                    component: () => import("../views/main/Account.vue")
                },
            ]
        },
        {
            path: "/user-guide",
            component: () => import("../views/user-guide/Index.vue"),
            children: [
                {
                    path: "index",
                    component: () => import("../views/user-guide/_partials/UserGuide.vue"),
                },
                {
                    path: "contact",
                    component: () => import("../views/user-guide/_partials/Contact.vue"),
                },
                {
                    path: "search",
                    component: () => import("../views/user-guide/_partials/Search.vue"),
                },
            ]
        },
        {
            path: "/oauth-redirect",
            component: () => import("../views/OAuthRedirect.vue"),
        },
        {
            path: "/oauth-success",
            component: () => import("../views/OAuthSuccess.vue"),
        },
        {
            path: "**",
            redirect: "/prepare"
        }
    ]
})

router.beforeEach(async (to, from, next) => {
    const urlUserGuides = ['/user-guide/index', '/user-guide/contact', '/user-guide/search']
    if (urlUserGuides.includes(to.path)) {
        return next()
    }
    if (['/oauth-redirect', "/oauth-success"].includes(to.path)) {
        return next()
    }
    const localAppConnected = await msDataProvider.getData("localAppConnected")
    if (!localAppConnected) {
        return nextIfNeed("/common/local_app_connect", to, next)
    }
    const setting = await msDataProvider.getData("setting");
    if (setting) {
        i18n.locale = setting.language ? setting.language : 'vi'
    }
    const localServiceConnected = await msDataProvider.getData("localServiceConnected")
    if (!localServiceConnected) {
        return nextIfNeed("/common/local_service_connect", to, next)
    }
    next()
})

router.onReady(() => {
    msDataProvider.watchData(
        "localAppConnected",
        value => {
            if (!value && router.currentRoute.path !== "/common/local_app_connect") {
                router.push("/common/local_app_connect");
            }
        },
        this
    );

    msDataProvider.watchData(
        "localServiceConnected",
        value => {
            if (
                !value &&
                router.currentRoute.path !== "/common/local_service_connect"
            ) {
                router.push("/common/local_service_connect");
            }
        },
        this
    );

    // msDataProvider.watchData(
    //   "sdpConnected",
    //   value => {
    //       if (!value && router.currentRoute.path !== "/common/sdp_disconnect") {
    //           router.push("/common/sdp_disconnect");
    //       }
    //   },
    //   this
    // );
})


export default router
